/**
 * CampaignStepDetail
 */
import { produce } from 'immer';
import {
  OmniElement,
  OmniStyleElement,
  OmniAppLayoutElement,
  OmniIconElement,
  OmniTooltipElement,
  classMap,
  html,
  repeat,
  css,
  nothing,
} from 'omni-ui';
import { Om2Dropdown, om2alert, om2confirm } from 'omni-campaign-ui';
import {
  wait,
  downloadElementAsImage,
  avoidBugHtml,
  trackPortalEvent,
} from '../helpers/utils.js';
import { selectStepModules } from '../helpers/selectors.js';

import CentralizedDataAPI from '../services/centralized-data-api.js';
import CampaignsAPI from '../services/campaigns-api.js';

import './campaign-empathy-map.js';
import './campaign-dynamic-form.js';
import './campaign-attach-objects.js';
import './campaign-attach-assets.js';
import './campaign-audience-insights.js';
import './campaign-input-card.js';
import './campaign-strategic-themes.js';
import './campaign-attach-images.js';
import './campaign-attention-calculator/campaign-attention-calculator.js';
import './campaign-step-status.js';
import './campaign-audience-persona/campaign-audience-persona.js';
import './campaign-assign-step-user.js';
import './campaign-exploration-viz/campaign-exploration-viz.js';
import './campaign-audience-viz/campaign-audience-viz.js';
import './campaign-attn-calc-multi-rec/campaign-attn-calc-multi-rec.js';
import './campaign-blueprint/campaign-blueprint.js';
import './campaign-thought-leader/campaign-thought-leader.js';
import './campaign-notes.js';
import './campaign-channel-wheel/channel-wheel.js';
import './campaign-sparks-ai.js';
import './campaign-idea-generator/campaign-idea-generator.js';

OmniStyleElement.register();
OmniAppLayoutElement.register();
OmniIconElement.register();
OmniTooltipElement.register();
Om2Dropdown.register();

export class CampaignStepDetail extends OmniElement {
  /**
   * Attach Objects Lit Properties
   * @property {string} env - Application environment
   * @property {Object} clientId - The client ID to be passed to the API so objects are scoped to the client
   * @property {Object} campaignData - Data associated with the current campaign
   * @property {Object} activeStepId - The ID of the currently active step (defined in framework template)
   * @property {Boolean} endDrawer - Determines if endDrawer component is open or closed
   * @property {string} token - API JWT token
   * @property {string} readonly - Indicates whether to render as readonly (use _isReadonly which is based on readonly)
   * @property {string} campaignId - The ID of the current campaign
   * @property {string} moduleId - The ID of the current module
   * @property {string} activeCampaignStep - The currently active step data from campaign
   * @property {string} dataCache -
   * @property {Object} allSharedObjects - shared objects that could be added to step
   * @property {Object} sharedObjects - shared objects that are added to the step
   * @property {string} currentSharedObject - currently selected shared object id
   * @property {string} selectedSharedObjects - store list of selected objects in 2-pane select (internal)
   * @property {Object} soData - currently selected shared object data
   * @property {Object} stepLocked - set if the current step is locked
   * @property {Object} stepLocked.by - the name of who locked the step
   * @property {Boolean} fullScreenModule - determines if the module is in full screen
   * @property {Array} moduleEditEvents - list of modules that have been edited
   */
  static get properties() {
    return {
      env: { type: String },
      clientId: { type: String },
      campaignData: { type: Object },
      activeStepId: { type: String },
      endDrawer: { attribute: false },
      token: { type: String },
      readonly: { type: Object },
      campaignId: { type: String },
      moduleId: { type: String },
      dataChanged: { type: Boolean },
      activeCampaignStep: { type: Object },
      dataCache: { type: Object },
      saveButtonText: { type: String },
      allSharedObjects: { type: Object },
      sharedObjects: { type: Object },
      currentSharedObject: { type: String },
      selectedSharedObjects: { state: true },
      soData: { type: Object },
      stepLocked: { type: Object },
      fullScreenModule: { state: true },
      moduleEditEvents: { state: true },
    };
  }

  /*
   * @returns {string} The campaign step id (different from this.activeStepId which is a framework template step id)
   */
  get campaignStepId() {
    return this.activeCampaignStep?.uuid_campaign_step;
  }

  /*
   * @returns {object} The currently active step framework template data
   */
  get activeStep() {
    return this.activeCampaignStep?.framework_template;
  }

  /*
   * @returns {object} The parent step (stage) of the currently active step in the framework template
   */
  get parentStep() {
    return this.activeCampaignStep?.framework_template?.parent;
  }

  /*
   * @returns {object} The campaign framework template data
   */
  get campaignFrameworkTemplate() {
    return this.campaignData?.framework_template;
  }

  /**
   * Step Detail module styles
   */
  static get styles() {
    return [
      super.styles,
      css`
        :host {
          --omni-app-layout-drawer-closed-width: 0px;
          --omni-app-layout-header-height: 100px;
          --omni-app-layout-height: calc(100vh - 50px);
          --omni-tile-body-gutter: 0px;
          --omni-app-layout-end-drawer-width: 31.857rem;
        }
        :host([full-screen-module]) {
          --omni-app-layout-header-height: 0px;
        }

        /* Fix issue where end-drawer animation adds scrollbars that remain */
        omni-app-layout.end-drawer-opening,
        omni-app-layout.end-drawer-closing {
          overflow: hidden;
        }

        main {
          position: relative;
          margin-bottom: 7.29rem;
        }
        :host([full-screen-module]) main {
          margin-bottom: 0;
        }

        omni-app-layout::part(header) {
          width: 100%;
          z-index: 6; /* Set higher than .bottom-content */
        }

        omni-app-layout::part(grid) {
          width: 100%;
        }

        /* Don't wrap the breadcrumbs unless necessary, omni-toolbar was reserving space
         * for the "center" slot even if unused. This gives more room to the end slot.
         */
        omni-toolbar::part(center) {
          flex-basis: 0%;
        }

        omni-toolbar::part(toolbar) {
          overflow: visible;
        }

        /* If the step description is very long, truncate & hide the text rather than expand the toolbar width */
        omni-toolbar.active-step-toolbar::part(start) {
          min-width: 0;
        }
        omni-toolbar.active-step-toolbar div[slot='start'],
        omni-toolbar.active-step-toolbar div[slot='start'] * {
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .save-interaction {
          min-width: 6.07rem;
        }

        :host omni-tile[maximized] {
          position: static;
          min-height: calc(100vh - 50px) !important;
          max-height: calc(100vh - 50px) !important;
        }

        .omni .buttons .button {
          margin-bottom: 0.5rem;
        }

        .end-drawer-header {
          display: flex;
          flex-flow: row nowrap;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0 !important;
        }

        .bottom-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background-color: rgb(var(--rgb-border));
          box-shadow: 0px -14px 35px -7px rgb(var(--rgb-border));
          padding: 1.79rem 2.86rem;
          position: absolute;
          z-index: 5;
          bottom: 0;
          width: 100%;
        }

        .end-drawer-header-title {
          color: rgb(var(--rgb-core-dark));
          line-height: 1.33;
          letter-spacing: 0.4px;
          text-transform: capitalize;
          font-weight: bold;
        }

        .shared-objects-dropdown {
          vertical-align: top;
          width: 225px;
        }
        campaign-step-status {
          width: 167px;
        }
        .shared-objects-dropdown om2-dropdown-impl::part(dropdown-trigger),
        .shared-objects-dropdown
          om2-dropdown-impl::part(dropdown-trigger-placeholder) {
          font-weight: bold;
          color: rgb(var(--rgb-core-dark)) !important;
        }

        .success-message {
          display: flex;
          justify-content: space-evenly;
          align-content: center;
          align-items: center;
        }

        .floating-module-buttons {
          position: absolute;
          right: 20px;
          margin-top: 25px;
          z-index: 1;
        }
        campaign-input-card::part(module-container) {
          width: 600px;
        }

        /* Temporary hack/fix: https://annalect.atlassian.net/browse/MN-1532 */
        div.main-content {
          min-height: 450px;
        }

        .soContainer {
          display: flex;
          flex-direction: row;
        }
      `,
    ];
  }

  /**
   * Create campaign-step-detail and initialize {@link #dataCache} and {@link #saveButtonText} properties
   */
  constructor() {
    super();
    this.dataCache = {};
    this.saveButtonText = 'Save';
    this.recentSharedObjects = [];
    this.moduleEditEvents = {};
  }

  /**
   * Initialize {@link #campaignsAPI} and {@link #centralizedDataAPI} on `this`
   *
   * @see {@link CentralizedDataAPI}
   */
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this._closeEndDrawer);
    this.addEventListener('omni-module-changed', this._handleModuleChange);

    this.campaignsAPI = new CampaignsAPI(this.token, this.env);
    this.centralizedDataAPI = new CentralizedDataAPI(this.token, this.env);

    this._fetchStepDetail();
  }

  disconnectedCallback() {
    this.removeEventListener('omni-module-changed', this._handleModuleChange);
    document.removeEventListener('click', this._closeEndDrawer);
    super.disconnectedCallback();
  }

  async updated(changedProperties) {
    if (
      changedProperties.has('activeStepId') &&
      changedProperties.get('activeStepId')
    ) {
      this._fetchStepDetail();
    }
    if (
      changedProperties.has('currentSharedObject') &&
      this.currentSharedObject
    ) {
      this.soData = null;
      this.soData = await this.campaignsAPI.getStepData(
        this.campaignStepId,
        this.currentSharedObject,
      );

      // Update "recent" list of shared objects (remove first to avoid duplicates)
      this.recentSharedObjects = this.recentSharedObjects.filter(
        id => id !== this.currentSharedObject,
      );
      this.recentSharedObjects.unshift(this.currentSharedObject);
    }
    if (
      changedProperties.has('sharedObjects') &&
      this.sharedObjects &&
      !this.currentSharedObject
    ) {
      // Attempt to auto-select a recently selected shared object
      const found = this.recentSharedObjects.find(id =>
        this.sharedObjects.some(so => so.uuid_object === id),
      );
      if (found) {
        this.currentSharedObject = found;
      }
    }
  }

  /**
   * Toggles show/hide for end drawer (right side)
   * @param {string} key - Determines which {@link endDrawer} to render based on assets from activeStep.assets
   * @listens module:CampaignStepDetail~event:click
   */
  _toggleEndDrawer(type) {
    if (type === this.endDrawer) {
      this.endDrawer = null;
    } else {
      this.endDrawer = type;
    }
  }

  /**
   * Determines sibling steps to manage next/previous button functionality
   * @param {string} direction - Previous or Next;  called on button click
   */
  _getSiblingStep(direction) {
    const { activeStep, parentStep, campaignFrameworkTemplate } = this;
    if (direction === 'next') {
      if (activeStep?.display_order < parentStep?.steps?.length) {
        return parentStep?.steps?.[activeStep?.display_order];
      }
      if (parentStep?.display_order < campaignFrameworkTemplate.steps?.length) {
        const uncle =
          campaignFrameworkTemplate.steps?.[parentStep.display_order];
        return uncle?.steps?.[0]; // First child of next uncle
      }
    } else if (direction === 'previous') {
      if (activeStep?.display_order > 1) {
        return (
          parentStep?.steps && parentStep.steps[activeStep.display_order - 2]
        );
      }
      if (parentStep?.display_order > 1) {
        const uncle =
          campaignFrameworkTemplate.steps?.[parentStep.display_order - 2];
        return uncle?.steps && uncle.steps[uncle.steps.length - 1]; // Last child of prev uncle
      }
    }
    return null;
  }

  /**
   * Triggers save button microinteraction; {@link #saveButtonText} changes after time specified by {@link #wait}
   */
  async _triggerMicroInteraction() {
    // change text and wait specified time before returning to original state
    this.saveButtonText = html`<omni-icon
      class="is-size-1"
      icon-id="omni:informative:check"
    ></omni-icon>`;
    await wait(2000);
    this.saveButtonText = 'Save';
  }

  /**
   * Handles change in active step triggered by page navigation
   * Calls {@link _scrollTopOmniTile} to force step view to top of page when navigating between steps
   * @listens module:campaign-route~event:activestepchange
   * @fires dispatchNewEvent#activestepchange
   * @param {string} stepUuid - UUID of active step; defaulted to null for navigation back to campaign overview page
   */
  _triggerActiveStepChange = (stepUuid = null) => {
    this.dispatchNewEvent('active-step-change', {
      detail: {
        id: stepUuid,
        parent_id: this.parentStep.uuid_step,
      },
    });
    // TODO currently scrolls page up before rerender to avoid refactoring the step change functionality
    this._scrollTopOmniTile();
  };

  /**
   * Handles scrolling to force step view to top of page
   */
  _scrollTopOmniTile = () => {
    const part = this.shadowRoot
      .querySelector('omni-tile')
      .shadowRoot.querySelector('[part="body-scroller"]');
    part.scroll(0, 0);
  };

  /**
   * Close end drawer on button click, window close, or click out of drawer area
   * @param {string} event - Event determined on mouseclick by global document event listener
   */
  _closeEndDrawer = event => {
    const endDrawer = this.shadowRoot.querySelector('.right-side-drawer');
    const drawerButtons = [
      ...this.shadowRoot.querySelectorAll('[data-drawer-button]'),
    ];
    if (
      // make sure event isn't fired from enddrawer
      !event.composedPath().includes(endDrawer) &&
      // make sure event isn't fired from enddrawer buttons
      !event.composedPath().some(element => drawerButtons.includes(element)) &&
      // make sure endDrawer is open
      this.endDrawer !== null
    ) {
      // set end drawer to null to close end drawer
      this.endDrawer = null;
    }
  };

  /**
   * Handles changes that occur at step level and updates {@link #dataCache} with new data to be saved
   * @listens connectedCallback
   */
  _handleModuleChange = e => {
    const { moduleId, saveAction, validateAction, moduleType } = e.detail;
    this.dataCache = produce(this.dataCache, draft => {
      draft[moduleId] = { saveAction, validateAction };
    });
    if (moduleType !== undefined) {
      this.moduleEditEvents[moduleType] = true;
    }
  };

  /**
   * Helper function to check if {@link #unsavedData} exists within dataCache object
   */
  _hasUnsavedData = () => !!Object.keys(this.dataCache).length;

  // function to check if a user has updated any form field on the page. triggers a warning if unsaved data exists.
  _validateBeforeAction(callback, warnFirst = true, clearCache = true) {
    if (warnFirst && this._hasUnsavedData()) {
      const action = () => {
        this._validateBeforeAction(callback, false, clearCache);
      };
      om2confirm(
        'If you continue without saving, your changes will be lost. How would you like to proceed?',
        {
          title: 'You have unsaved changes',
          type: 'warning',
          callback: [
            {
              label: 'Continue',
              func: action,
            },
            {
              label: 'Save',
              func: async () => {
                if (await this._submitData()) {
                  action();
                }
              },
            },
          ],
        },
      );
    } else if (callback && typeof callback === 'function') {
      if (clearCache) this.dataCache = {};
      callback();
    }
  }

  /**
   * Updates the modified_date in the Shared Objects db
   * @param {string} uuidCampaign - The ID of the current campaign
   *
   */
  async _updateSharedObjectModule(uuidCampaign) {
    // Avoid creating a shared object for local campaigns
    if (this.env === 'local') return;

    const payload = {
      update_date: new Date().toISOString(),
      guid_person: window.AN.sso.user.personId,
      external_id: uuidCampaign,
      permissions: [this.clientId],
    };

    try {
      await this.centralizedDataAPI.updateServiceSharedObject(
        payload,
        this.campaignData.so_type,
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`Failed to update user shared object`, e);
    }
  }

  _onAsyncEnumHydration(e) {
    this.activeCampaignStep = produce(this.activeCampaignStep, draftStep => {
      const activeModule =
        draftStep.framework_template.step_modules[e.detail.moduleIndex];
      activeModule.module.data_structure.schema[e.detail.key].enum =
        e.detail.options;
      activeModule.module.data_structure.schema.hydrated = true;
    });
  }

  /**
   * Saves newly submitted user data; called when user clicks save, next, prev, and bread crumbs. Throws new erorr if save fails.
   *
   */
  async _submitData() {
    try {
      // Cache data so re-render isn't triggered prematurely
      const tempCache = { ...this.dataCache };

      // trigger text change on initial click
      this.saveButtonText = 'Saving...';

      // First validate all modules *before* we attempt to save if the module provided
      // a validateAction callback. If any validation fails, do not continue.
      for (const { validateAction } of Object.values(this.dataCache)) {
        if (validateAction && !validateAction()) {
          // Stop here, but don't open an alert. The module should decide how best to
          // communicate the problem to the user.
          this.saveButtonText = 'Save';
          return false;
        }
      }

      for (const [moduleId, { saveAction }] of Object.entries(this.dataCache)) {
        // Go sequentially through modules and stop if one fails. This however
        // means that we might end up saving some modules and not others. The validation
        // step above should minimize this possibility.

        // wait for save action to complete
        // TODO: (JRH) Revisit await in loop
        await saveAction(); // eslint-disable-line no-await-in-loop

        // Remove the save action from our cache after successful save
        delete tempCache[moduleId];
      }
      // Fire the trackPortalEvent for each moduleType in the cache
      Object.keys(this.moduleEditEvents).forEach(moduleType => {
        trackPortalEvent('content', 'edit', moduleType);
      });
      // if successful, call triggerMicroInteraction to proceed with microinteraction
      trackPortalEvent('save button', 'save', 'step');
      await this._triggerMicroInteraction();

      // Clear the moduleEditEvents after firing the events
      this.moduleEditEvents = {};
      // clear data upon successful save
      this.dataCache = tempCache;

      // Updates shared object modified date
      this._updateSharedObjectModule(this.campaignId);

      return true;
    } catch (e) {
      this.saveButtonText = 'Save';
      om2alert(
        `Sorry, your work was not saved. Please check your work and try again.`,
        { closeTimeout: 3000 },
      );
      console.error(e); // eslint-disable-line no-console
    }
    return false;
  }

  // fetch field/form data for campaign modules associated with current step
  async _fetchStepDetail() {
    const { user } = AN.sso;
    try {
      this.isLoading = true;
      const stepData = await this.campaignsAPI.getStep(
        this.campaignId,
        this.activeStepId,
        user,
      );
      const { user_locked: userLocked } = stepData;
      if (userLocked && userLocked?.guid_person !== String(user?.personId)) {
        const { first_name: firstName, last_name: lastName } = userLocked;
        const msg = `This step is currently being worked on by ${firstName} ${lastName}. Once they have completed their changes, the step will unlock and you will be able to begin your work.`;
        om2confirm(msg, {
          title: 'This step is locked',
          type: 'warning',
          hideCancel: true,
        });
        this.stepLocked = { by: `${firstName} ${lastName}` };
      } else if (this.stepLocked) {
        this.stepLocked = undefined;
      }
      this.activeCampaignStep = stepData;

      // Reset & then load shared objects for the step if there are any
      this.allSharedObjects = undefined;
      this.sharedObjects = undefined;
      this.currentSharedObject = undefined;
      this.soData = undefined;
      this._loadSharedObjectProperties(
        this.activeStep?.data_points?.shared_object,
      );
    } catch (e) {
      // TODO: customize message and alert type based on status code
      console.log(e); // eslint-disable-line no-console
      om2alert(
        `Unexpected error fetching requested step (${this.activeStepId})`,
        { close: () => this._triggerActiveStepChange() },
      );
    }
    this.isLoading = false;
  }

  _loadSharedObjectProperties(soInfo) {
    if (!soInfo) return;

    // Load list of all shared objects available if we haven't already
    if (!this.allSharedObjects) {
      const params = {
        client: this.clientId,
        category: soInfo.uuid_category,
        subcategory: soInfo.uuid_sub_category,
      };
      this.centralizedDataAPI.getSharedObjects(params).then(result => {
        this.allSharedObjects = result.data;
      });
    }

    // Load list of shared objects added to this step if we haven't yet & select first
    if (!this.sharedObjects) {
      this.campaignsAPI
        .getStepSharedObjects(this.campaignId, soInfo.uuid_sub_category)
        .then(result => {
          this.sharedObjects = result.data;
          this.currentSharedObject = this.sharedObjects?.[0]?.uuid_object;
        });
    }
  }

  /*
   * More complex way of determining whether the current step ought to be displayed as
   * readonly. If the user has limited general access "this.readonly" will be true,
   * however if that same user was added to the step users list explicitly, they will
   * then have normal read/write access. If the step is locked it is readonly for all.
   *
   * All code that is testing whether to render as readonly should depend on this
   * calculation rather than the basic this.readonly property which is passed by portal.
   */
  get _isReadonly() {
    const currentUser = window.AN?.sso?.user?.personId;
    const isAddedUser =
      this.activeCampaignStep?.client_users?.includes?.(currentUser) ?? false;
    return (this.readonly && !isAddedUser) || this.stepLocked;
  }

  _renderVizButtons(moduleType, moduleUuid) {
    const vizTypes = [
      'attention_calculator',
      'audience_insights',
      'audience_persona',
      'audience_viz',
      'empathy_map',
      'exploration_viz',
      'strategic_themes',
      'attention_calculator_multi_rec',
      'thought_leader',
      'notes',
      'channel_wheel',
      'blueprint',
    ];
    if (!vizTypes.includes(moduleType)) return nothing;

    const isFullScreen = moduleUuid === this.fullScreenModule;

    return html`
      <div class="floating-module-buttons">
        <omni-tooltip class="full-screen">
          <button
            class="button is-text"
            slot="invoker"
            @click=${async e => {
              if (isFullScreen) {
                this.fullScreenModule = undefined;
                this.removeAttribute('full-screen-module');
              } else {
                this.fullScreenModule = moduleUuid;
                this.setAttribute('full-screen-module', '');
              }
              await this.requestUpdate(); // Wait for elements to be [un]hidden
              e.target.scrollIntoView({ block: 'center' });
            }}
          >
            <omni-icon
              class="is-size-2"
              icon-id=${isFullScreen
                ? 'omni:interactive:minimize'
                : 'omni:interactive:expand'}
            ></omni-icon>
          </button>
          <div slot="content">
            ${isFullScreen ? 'Exit full screen' : 'View in full screen'}
          </div>
        </omni-tooltip>

        <omni-tooltip>
          <button
            class="button is-text"
            slot="invoker"
            @click=${async e => {
              const btn = e.target.closest('button');
              const icon = btn.firstElementChild;
              btn.classList.add('is-loading');
              icon.classList.add('is-hidden');
              const root = e.target.closest(
                '.floating-module-buttons',
              ).nextElementSibling;
              try {
                let exported = false;
                if (root.exportModule) {
                  // If exportModule() returns null fall back to image export
                  exported = (await root.exportModule()) !== null;
                }
                if (!exported) {
                  await downloadElementAsImage(
                    root?.exportImageRootElement ?? root,
                    `${this.campaignData.name}_${moduleType.replaceAll(
                      '-',
                      '_',
                    )}.png`,
                  );
                }
              } finally {
                btn.classList.remove('is-loading');
                icon.classList.remove('is-hidden');
              }
            }}
          >
            <omni-icon
              class="is-size-2"
              icon-id="omni:interactive:download"
            ></omni-icon>
          </button>
          <div slot="content">Export</div>
        </omni-tooltip>
      </div>
    `;
  }

  /**
   * Conditional render to check if "guide" assets exist on activeStep
   * @param {boolean} isActive - Determines whether or not the asset sidebar is active (true) or inactive (false)
   * @param {string} tooltip - Determines tooltip for associated asset
   * @param {string} icon - Determines icon for associated asset
   * @param {Function} handleClick - Determines what happens on button click
   */
  _renderButton = (isActive, tooltip, icon, handleClick) => html`
    <omni-tooltip slot="end">
      <button
        slot="invoker"
        class=${classMap({
          button: true,
          'is-text': true,
          'has-background-light-blue': true,
          'is-active': isActive,
        })}
        @click=${handleClick}
        data-drawer-button
      >
        <omni-icon class="is-size-1" icon-id=${icon}></omni-icon>
      </button>
      <div slot="content">${tooltip}</div>
    </omni-tooltip>
  `;

  // renders end drawer content based on parameter in this.toggleEndDrawer
  // TODO add info and path functionality
  _renderEndDrawer(activeStep) {
    if (this.fullScreenModule) return nothing;

    if (this.endDrawer === 'assets') {
      return html`
        <div class="end-drawer-header">
          <p class="end-drawer-header-title is-size-4 p-2">Your Guides</p>
          <button
            class="button is-text is-alt-1"
            @click=${() => this._toggleEndDrawer('assets')}
          >
            <omni-icon
              class="is-size-2"
              icon-id="omni:interactive:remove"
            ></omni-icon>
          </button>
        </div>

        ${(activeStep.assets || []).map(
          asset => html`
            <div class="box p-3">
              <p class="title is-size-5">${asset.name}</p>
              <p class="subtitle is-size-6">${asset.description}</p>
              ${asset.type === 'link'
                ? html`
                    <div class="has-text-right">
                      <button
                        class="button is-text"
                        @click=${() => {
                          window.open(asset.asset, '_blank').focus();
                        }}
                      >
                        <omni-icon
                          class="is-size-2"
                          icon-id="omni:interactive:launch"
                        ></omni-icon>
                      </button>
                    </div>
                  `
                : nothing}
            </div>
          `,
        )}
      `;
    }
    // return null to avoid rendering empty object
    return null;
  }

  #renderShareDropdown = () => {
    const link = window.location.href;
    const options = [
      {
        label: 'Share via Email',
        value: 'email',
      },
      {
        label: 'Copy Link',
        value: 'copy',
      },
    ];

    // is-relative class due to omni-ui important code
    return html`
      <div class="is-relative" slot="end">
        <om2-icon-menu
          .options="${options}"
          iconId="omni:interactive:share"
          iconClass="is-size-1"
          tooltip="Share"
          @change=${e => {
            if (e.detail.value.value === 'email') {
              trackPortalEvent('email link', 'share', 'step');
              window.location.href = `mailto:?subject=Let's chat about ${this.campaignData.name}&body=Here's a link to the work in Omni: ${link}`;
            } else {
              navigator.clipboard.writeText(link);
              trackPortalEvent('copy link', 'share', 'step');
              om2alert('Link is copied to clipboard.', {
                type: 'success',
                toast: true,
                closeTimeout: 3000,
              });
            }
          }}
        >
        </om2-icon-menu>
      </div>
    `;
  };

  /**
   * @description function invoked within the Teams _renderButton(). Fires an event, teams-modal-open, that bubbles up to portal passing up an object, campaignObject, with data needed to build out the Adaptive Card that gets sent over Teams.
   */
  _dispatchTeamsEvent = () => {
    const { start_date: startDate, end_date: endDate } = this.campaignData;

    const { display_order_label: stepOrder, name: stepName } = this.activeStep;

    const { name: stageName } = this.parentStep;

    const campaignObject = {
      ...this.campaignData,
      campaign_step_data: {
        step: `${stageName} | ${stepOrder}: ${stepName}`,
        duration: `${startDate} to ${endDate}`,
      },
      type: 'Campaign',
      url: window.location.href,
    };
    trackPortalEvent('teams', 'share', 'step');
    this.dispatchNewEvent('teams-modal-open', {
      detail: campaignObject,
      bubbles: true,
      composed: true,
    });
  };

  /**
   * fetches and downloads the campaigns powerpoint template
   * @param {MouseEvent} e - event pass by button click
   * @returns out of the function to avoid calling exportCampaign multiple times
   */
  _exportPPTX = async e => {
    const button = e.target.closest('button');
    if (button.classList.contains('is-loading')) return; // Ignore clicks while await-ing
    button.classList.add('is-loading', 'is-active');
    try {
      await this.campaignsAPI.exportCampaign(
        this.campaignId,
        this.activeStepId,
      );
    } catch (ex) {
      om2alert(ex.message);
    }
    button.classList.remove('is-loading', 'is-active');
    e.target.blur();
  };

  _appLinksTemplate(activeStep) {
    return html` ${Object.values(activeStep?.apps || {}).map(
        ({ icon_class: iconClass, name, url }) =>
          this._renderButton(false, name, iconClass, () => {
            // Relative url add client; full url no prefix; else add protocol
            let prefix = '';
            if (url.startsWith('/')) {
              prefix = `/${this.clientId}`;
            } else if (!url.match(/^https?:\/\//i)) {
              prefix = 'https://';
            }
            trackPortalEvent('app link', 'open', 'step');
            window.open(`${prefix}${url}`, '_blank', 'noopener,noreferrer');
          }),
      )}
      <!-- conditionally render pipe -->
      ${activeStep?.assets?.length > 0
        ? html`<div slot="end" class="toolbar-divider"></div>`
        : null}`;
  }

  _teamsAndGuidesTemplate(activeStep) {
    return html` <!-- render teams icon -->
      ${this._renderButton(
        false,
        'Collaborate in Teams',
        'omni:app:teams',
        this._dispatchTeamsEvent,
      )}
      <!-- render guides button -->
      ${activeStep?.assets?.length > 0
        ? this._renderButton(
            this.endDrawer === 'assets',
            'Guides',
            'omni:informative:book',
            () => {
              trackPortalEvent('guides', 'open', 'step');
              return this._toggleEndDrawer('assets');
            },
          )
        : null}
      <!-- render export -->
      ${this.campaignFrameworkTemplate.ppt_url
        ? this._renderButton(
            false,
            'Export PPTX',
            'omni:interactive:export',
            e => {
              trackPortalEvent('ppt', 'export', 'step');
              return this._exportPPTX(e);
            },
          )
        : null}
      <!-- Assign user to a step -->
      <campaign-assign-step-user
        slot="end"
        .env=${this.env}
        .token=${this.token}
        .campaign=${this.campaignData}
        .step=${this.activeCampaignStep}
        .readonly=${this._isReadonly}
        .clientId=${this.clientId}
        @client-users-change=${e => {
          trackPortalEvent('client user', 'assign', 'step');
          this.activeCampaignStep = produce(
            this.activeCampaignStep,
            draftData => {
              draftData.client_users = e.detail.ids;
            },
          );
        }}
        @teams-users-change=${e => {
          trackPortalEvent('team member', 'assign', 'step');
          this.activeCampaignStep = produce(
            this.activeCampaignStep,
            draftData => {
              draftData.teams_users = e.detail.ids;
            },
          );
        }}
      >
      </campaign-assign-step-user>`;
  }

  _headerLinksTemplate(activeStep) {
    return html`
      <!-- render associated app links -->
      ${this._appLinksTemplate(activeStep)}
      <!-- render share icon -->
      ${this.#renderShareDropdown()}
      <!-- if user not read only, render guides, discuss in teams, assign user icons -->
      ${!this.readonly ? this._teamsAndGuidesTemplate(activeStep) : nothing}
    `;
  }

  /**
   * Render method specifically for step detail header that includes title text, app links, etc
   * @param {string} activeStep - Determines the activeStep using {@link #activeStep} to determine step display order
   * @returns {html} Rendered HTML for the {@link header} component
   */
  _renderHeader(activeStep, parentStep) {
    if (this.fullScreenModule) return nothing;

    const stripPrefix = label => {
      /*
       * Try to pull out the trailing number from the label.
       *   "Decision 1" => "1"
       *   "Step IV:" => "IV"
       *   "Part (b.)" => "b"
       *   "*Unexpected Text*" => "*Unexpected Text*"
       */
      const match = label?.match(
        /\s[.:()!?]?([0-9]+|[A-Z]+|[a-z]+)\s*[.:()!?]*$/,
      );
      return match ? match[1] : label ?? '';
    };

    return html`
      <omni-toolbar>
        <button
          slot="start"
          class="button is-text is-shadowless"
          @click=${() => {
            trackPortalEvent('arrow button', 'return', 'step');
            this._validateBeforeAction(this._triggerActiveStepChange);
          }}
        >
          <omni-icon
            class="is-size-1"
            icon-id="omni:interactive:back"
          ></omni-icon>
        </button>
        <button
          class="button is-primary"
          @click=${() => {
            trackPortalEvent('stage name', 'open', 'step');
            this._validateBeforeAction(this._triggerActiveStepChange);
          }}
        >
          ${parentStep?.name}
        </button>
        <nav
          slot="end"
          class="breadcrumb has-succeeds-separator is-small"
          aria-label="steps"
        >
          <ul class="m-0 p-0">
            ${parentStep?.steps?.length
              ? parentStep.steps.map(
                  (step, i) => html`
                    <li
                      class="${i === activeStep.display_order - 1
                        ? 'is-active'
                        : ''}"
                    >
                      <a
                        @click=${() => {
                          trackPortalEvent('breadcrumb', 'open', 'step');
                          this._validateBeforeAction(() =>
                            this._triggerActiveStepChange(step.uuid_step),
                          );
                        }}
                        >${stripPrefix(step.display_order_label)}.
                        ${step.name}</a
                      >
                    </li>
                  `,
                )
              : nothing}
          </ul>
        </nav>
      </omni-toolbar>
      <omni-toolbar class="active-step-toolbar">
        <div slot="start" class="is-flex">
          ${this.stepLocked
            ? html`<omni-tooltip place="right" relateby="label">
                <omni-icon
                  slot="invoker"
                  class="is-size-2 is-warning is-clickable ml-4 mr-5 mt-1"
                  icon-id="omni:interactive:lock"
                ></omni-icon>
                <div slot="content">Step locked by ${this.stepLocked.by}</div>
              </omni-tooltip>`
            : ''}
          <div>
            <h2 class="title is-size-2">
              ${activeStep?.display_order_label} : ${activeStep?.name}
            </h2>
            <h4 class="subtitle is-size-5">${activeStep?.description}</h4>
          </div>
        </div>
        <!-- render app links -->
        ${this._headerLinksTemplate(activeStep)}
      </omni-toolbar>
    `;
  }

  async _saveAudienceSegments(segmentIds, soInfo) {
    // Replace the list of shared objects for the step
    try {
      const resp = await this.campaignsAPI.setCampaignSharedObjects(
        this.campaignId,
        soInfo.uuid_sub_category,
        segmentIds,
      );
      this.sharedObjects = resp.data;
      this.currentSharedObject = this.sharedObjects?.[0]?.uuid_object;

      if (
        !this.sharedObjects.some(
          obj => obj.uuid_object === this.currentSharedObject,
        )
      ) {
        // Reset shared object state if currently selected object is no longer valid
        this.currentSharedObject = undefined;
        this.soData = undefined;
      }
    } catch (e) {
      om2alert('Failed to add shared object to step');
      throw e;
    }
  }

  _renderAudienceSegments(soInfo) {
    if (!soInfo) return nothing;

    let addOptionFromSearch;
    if (!soInfo.uuid_api_connection) {
      addOptionFromSearch = {
        label: search =>
          `${soInfo.input_label ?? 'Add new segment'} "${search}"`,
        callback: async (search, self) => {
          let newUUID;
          // Create user shared objected
          if (!window.AN?.sso?.user?.personId) {
            throw new Error("Missing personId: can't save user shared object");
          }
          const params = {
            name: search,
            uuid_category: soInfo.uuid_category,
            uuid_sub_category: soInfo.uuid_sub_category,
            guid_person: window.AN.sso.user.personId,
            guid_client: this.clientId,
            status: 'Active',
          };
          try {
            const resp = await this.centralizedDataAPI.createUserSharedObject(
              params,
            );
            newUUID = resp.uuid_object;
          } catch (e) {
            om2alert('Failed to create user shared object');
            throw e;
          }
          this.allSharedObjects = produce(this.allSharedObjects, draft => {
            draft.push({ uuid_object: newUUID, name: search });
          });
          this.selectedSharedObjects = [...self.value, newUUID];
        },
      };
    }

    return html`
      <div class="soContainer">
        <campaign-input-card
          class="is-inline-block"
          .readonly=${this._isReadonly}
          headerText=${soInfo.title ?? 'Manage Segments'}
          confirmBtnText="Save"
          openBtnIcon="omni:informative:settings"
          tooltipText="Manage Segments"
          .handleSubmit=${() => {
            const twopane = this.shadowRoot.getElementById(
              'shared-object-two-pane-select',
            );
            this._saveAudienceSegments(twopane.value, soInfo);
          }}
          @campaign-input-card-open=${() => {
            // Reset state of two-pane select when openning card since the card
            // doesn't remove and re-create its content on open/close.
            const twopane = this.shadowRoot.getElementById(
              'shared-object-two-pane-select',
            );
            twopane.search = '';
            this.selectedSharedObjects = this.sharedObjects?.map(
              so => so.uuid_object,
            );
          }}
          @campaign-input-card-close=${() => {
            this._scrollTopOmniTile();
          }}
        >
          ${this.allSharedObjects && this.sharedObjects
            ? html`
                <div class="mb-5 has-text-grey-light">
                  Select audience segments you would like to show or hide in the
                  options list.
                </div>
                <om2-two-pane-select
                  id="shared-object-two-pane-select"
                  .options=${this.allSharedObjects}
                  nameProperty="name"
                  idProperty="uuid_object"
                  .value=${this.selectedSharedObjects}
                  @change=${e => {
                    this.selectedSharedObjects = e.target.value;
                  }}
                  leftPaneLabel="Available segments"
                  rightPaneLabel="Selected audience segments"
                  searchPlaceholder=${addOptionFromSearch
                    ? 'Search or add new segment'
                    : 'Search segments'}
                  .rightPanePlaceholder="${html`No audiences have been
                    selected.<br />Search and select existing<br />${addOptionFromSearch
                      ? 'or add a new one'
                      : ''}.`}"
                  .addOptionFromSearch="${addOptionFromSearch}"
                >
                </om2-two-pane-select>
              `
            : nothing}

          <div class="m-4"></div>
        </campaign-input-card>

        ${this.sharedObjects && this.sharedObjects.length > 0
          ? html` <om2-dropdown
              borderless
              class="is-inline-block mb-5 shared-objects-dropdown"
              nameProperty="name"
              idProperty="uuid_object"
              .options=${this.sharedObjects}
              .value=${this.currentSharedObject}
              @change=${e =>
                this._validateBeforeAction(() => {
                  this.currentSharedObject = e.detail.value;
                })}
            >
            </om2-dropdown>`
          : html`<div class="subtitle is-size-5 mt-2">
              Select an output from the dropdown to add content to this step
            </div>`}
      </div>
    `;
  }

  renderModule(module, savedData, context, saveImpl) {
    const {
      module: {
        module_type: moduleType,
        data_structure: {
          treat_action_url_as_external: treatActionURLAsExternal,
          uuid_api_connection: uuidApiConnection,
          category,
          subcategories,
          placeholder,
          title,
          label,
          defaultData,
          schema,
          uiSchema,
        } = {},
      } = {},
    } = module;

    // TODO Remove avoidBugHtml when we verify chrome bug is resolved
    // TODO convert existing modules to utilize schema
    // TODO Move this switch statement and list of "viz" modules to its own file.
    switch (moduleType) {
      case 'blueprint':
        return avoidBugHtml`<campaign-blueprint
          .savedData=${savedData}
          .env=${this.env}
          .token=${this.token}
          .context=${context}
          .save=${saveImpl}
        >
        </campaign-blueprint>`;
      case 'attach_docs':
        return avoidBugHtml`<campaign-attach-assets
          .savedData=${savedData}
          .env=${this.env}
          .token=${this.token}
          .context=${context}
          .save=${saveImpl}
          .schema=${schema}
          .moduleTitle=${title}
        >
        </campaign-attach-assets>`;
      case 'attach_images':
        return avoidBugHtml`<campaign-attach-images
          .context=${context}
          .env=${this.env}
          .save=${saveImpl}
          .savedData=${savedData}
          .schema=${schema}
          .moduleTitle=${title}
        ></campaign-attach-images> `;
      case 'attach_objects':
        return avoidBugHtml`<campaign-attach-objects
          .moduleTitle=${title}
          .label=${label}
          .placeholder=${placeholder}
          .env=${this.env}
          .savedData=${savedData}
          .token=${this.token}
          .objectApiUUID=${uuidApiConnection}
          .treatActionURLAsExternal=${treatActionURLAsExternal}
          .category=${category}
          .subcategories=${subcategories}
          .context=${context}
          .save=${saveImpl}
        >
        </campaign-attach-objects>`;
      case 'attention_calculator':
        return avoidBugHtml`<campaign-attention-calculator
          .savedData=${savedData}
          .env=${this.env}
          .token=${this.token}
          .context=${context}
          .save=${saveImpl}
          .schema=${schema}
        >
        </campaign-attention-calculator>`;
      case 'attention_calculator_multi_rec':
        return avoidBugHtml`<campaign-attn-calc-multi-rec
          .savedData=${savedData}
          .env=${this.env}
          .token=${this.token}
          .context=${context}
          .save=${saveImpl}
          .schema=${schema}
        >
        </campaign-attn-calc-multi-rec>`;
      case 'audience_insights':
        return avoidBugHtml`
          <campaign-audience-insights
            .moduleTitle=${title}
            .savedData=${savedData}
            .env=${this.env}
            .token=${this.token}
            .context=${context}
            .save=${saveImpl}
            .schema=${schema}
          >
          </campaign-audience-insights> `;
      case 'audience_viz':
        return avoidBugHtml`
          <campaign-audience-viz
            .savedData=${savedData}
            .env=${this.env}
            .token=${this.token}
            .context=${context}
            .save=${saveImpl}
            .schema=${schema}
          >
          </campaign-audience-viz> `;
      case 'empathy_map':
        return avoidBugHtml`
          <campaign-empathy-map
            .moduleTitle=${title}
            .savedData=${savedData}
            .env=${this.env}
            .token=${this.token}
            .context=${context}
            .save=${saveImpl}
            .schema=${schema}
          >
          </campaign-empathy-map> `;
      case 'exploration_viz':
        return avoidBugHtml`
          <campaign-exploration-viz
            .savedData=${savedData}
            .context=${context}
            .env=${this.env}
            .save=${saveImpl}
            .schema=${schema}
          >
          </campaign-exploration-viz> `;
      case 'form':
        return avoidBugHtml`<campaign-dynamic-form
          @asyncenumhydration=${this._onAsyncEnumHydration}
          .env=${this.env}
          .data=${savedData}
          .schema=${schema}
          .uiSchema=${uiSchema}
          .token=${this.token}
          .context=${context}
          .save=${saveImpl}
          hidesubmit
        >
        </campaign-dynamic-form>`;
      case 'audience_persona':
        return avoidBugHtml`
          <campaign-audience-persona
            .savedData=${savedData}
            .save=${saveImpl}
            .env=${this.env}
            .context=${context}
          ></campaign-audience-persona>`;
      case 'strategic_themes':
        return avoidBugHtml`
          <campaign-strategic-themes
            .moduleTitle=${title}
            .label=${label}
            .donutPlaceholder=${placeholder}
            .env=${this.env}
            .savedData=${savedData}
            .token=${this.token}
            .context=${context}
            .defaultData=${defaultData}
            .save=${saveImpl}
          >
          </campaign-strategic-themes>`;
      case 'thought_leader':
        return avoidBugHtml`
          <campaign-thought-leader
            .moduleTitle=${title}
            .savedData=${savedData}
            .env=${this.env}
            .token=${this.token}
            .context=${context}
            .save=${saveImpl}
            .schema=${schema}
          >
          </campaign-thought-leader> `;
      case 'notes':
        return avoidBugHtml`
              <campaign-notes
                .moduleTitle=${title}
                .savedData=${savedData}
                .env=${this.env}
                .token=${this.token}
                .context=${context}
                .save=${saveImpl}
                .schema=${schema}
                .step=${this.activeCampaignStep}
                .campaign=${this.campaignData}
              >
              </campaign-notes> `;
      case 'channel_wheel':
        return avoidBugHtml`
          <campaign-channel-wheel
            .savedData=${savedData}
            .env=${this.env}
            .token=${this.token}
            .context=${context}
            .save=${saveImpl}
            .schema=${schema}
          >
          </campaign-channel-wheel>`;
      case 'sparks_ai':
        return html`
          <campaign-sparks-ai
            .savedData=${savedData}
            .env=${this.env}
            .token=${this.token}
            .context=${context}
            .save=${saveImpl}
          >
          </campaign-sparks-ai>
        `;
      case 'idea_generator':
        return avoidBugHtml`<idea-generator
          .savedData=${savedData}
          .env=${this.env}
          .token=${this.token}
          .context=${context}
          .save=${saveImpl}
          .schema=${schema}
        >
        </idea-generator>`;

      default:
        return nothing;
    }
  }

  /**
   * @returns {html} Rendered HTML for the component
   */
  render() {
    const { activeStep, parentStep } = this;
    const prevStep = this._getSiblingStep('previous');
    const nextStep = this._getSiblingStep('next');
    const soInfo = this.activeStep?.data_points?.shared_object; // Shared Object Info
    let activeModules = selectStepModules(activeStep);

    // If step is configured with shared objects, but we haven't yet loaded the step data
    // for a particular shared object, don't render any modules.
    if (soInfo && !this.soData) activeModules = [];

    return html`
      <omni-style>
        <omni-tile maximized scroll>
          <omni-app-layout .endDrawerOpen=${!!this.endDrawer}>
            <header slot="header">
              ${this._renderHeader(activeStep, parentStep)}
            </header>
            <main>
              <div class="m-5">
                <div class="main-content is-relative">
                  ${this._renderAudienceSegments(soInfo)}
                  ${soInfo &&
                  !this.soData &&
                  (!this.sharedObjects || this.sharedObjects?.length)
                    ? html`<omni-loading-indicator></omni-loading-indicator>`
                    : nothing}
                  ${
                    /* Use repeat() so we can specify a key function that tells lit when to destroy/re-create our module dom elements */
                    repeat(
                      activeModules ?? [],
                      module =>
                        `${module.module.uuid_module};${this.campaignStepId}`,
                      (module, i) => {
                        const { uuid_step: uuidStep } = activeStep;
                        const uuidModule = module.module.uuid_module;
                        let savedData = module.saved_data;
                        const context = {
                          uuid_step: uuidStep,
                          uuid_module: uuidModule,
                          moduleIndex: i,
                          clientId: this.clientId,
                          readonly: this._isReadonly,
                          sharedData: this.campaignData.shared_data,
                          campaignData: this.campaignData,
                          assistFields: module.module?.assist_fields ?? [],
                        };

                        // If the step has shared objects, use the saved data from the currently selected shared object step data
                        if (soInfo) {
                          savedData = this.soData[uuidModule]; // eslint-disable-line no-param-reassign
                        }

                        const saveImpl = async (data, sharedData) => {
                          try {
                            await this.campaignsAPI.saveStepModule(
                              this.campaignStepId,
                              this.currentSharedObject,
                              uuidModule,
                              data,
                            );

                            if (
                              sharedData &&
                              Object.keys(sharedData).length > 0
                            ) {
                              await this.campaignsAPI.saveSharedData(
                                this.campaignId,
                                this.campaignStepId,
                                uuidModule,
                                sharedData,
                              );
                              this.dispatchNewEvent('shared-data-change', {
                                detail: {
                                  sharedData,
                                  uuidModule,
                                  campaignStepId: this.campaignStepId,
                                },
                              });
                            }
                          } catch (e) {
                            console.error(e); // eslint-disable-line no-console
                            throw Error(
                              'Save failed unexpectedly. Please try again.',
                            );
                          }
                        };
                        return html` <div
                          class=${classMap({
                            'is-hidden':
                              this.fullScreenModule &&
                              this.fullScreenModule !== uuidModule,
                          })}
                        >
                          ${this._renderVizButtons(
                            module.module.module_type,
                            uuidModule,
                          )}
                          ${this.renderModule(
                            module,
                            savedData,
                            context,
                            saveImpl,
                          )}
                        </div>`;
                      },
                    )
                  }
                </div>
              </div>
            </main>
            ${!this.isLoading && !this.fullScreenModule
              ? html`<div class="bottom-content">
                  <campaign-step-status
                    .stepId=${this.activeStepId}
                    .campaign=${this.campaignData}
                    .readonly=${this._isReadonly}
                    .token=${this.token}
                    .env=${this.env}
                  >
                  </campaign-step-status>
                  <div class="buttons is-right">
                    <button
                      class="button is-outlined"
                      ?disabled=${!prevStep}
                      @click=${() =>
                        prevStep &&
                        this._validateBeforeAction(() => {
                          trackPortalEvent('previous button', 'return', 'step');
                          return this._triggerActiveStepChange(
                            prevStep.uuid_step,
                          );
                        })}
                    >
                      Prev
                    </button>
                    <button
                      class="button is-outlined"
                      ?disabled=${!nextStep}
                      @click=${() =>
                        nextStep &&
                        this._validateBeforeAction(() => {
                          trackPortalEvent('next', 'open', 'step');
                          return this._triggerActiveStepChange(
                            nextStep.uuid_step,
                          );
                        })}
                    >
                      Next
                    </button>
                    <button
                      ?disabled=${!this._hasUnsavedData()}
                      class="button is-primary save-interaction"
                      @click=${this._submitData}
                    >
                      ${this.saveButtonText}
                    </button>
                  </div>
                </div>`
              : nothing}
            <aside slot="end-drawer" class="m-3 right-side-drawer">
              ${this._renderEndDrawer(activeStep)}
            </aside>
          </omni-app-layout>
        </omni-tile>
      </omni-style>
    `;
  }
}

OmniElement.register('campaign-step-detail', CampaignStepDetail);
